/**
 * Formatting utilities for video annotation and documentation.
 * Uses shared formatters to ensure consistency between documentation and actual output.
 */

import { TextAlignment, VideoData } from '@lumen5/luminary';

import { ASSET_TYPES } from '../constants';
import type {
    CaptionDescription,
    ColorAssetDescription,
    DocumentationExample,
    GroupAssetDescription,
    IconAssetDescription,
    MediaAssetDescription,
    StickerAssetDescription,
    TextAssetDescription,
} from '../types';

import { formatAsset } from './assetFormatters';

/**
 * Shown only on assets that do not simply appear with their scene, so its absence reads as
 * "visible from the scene's first frame" rather than as missing information.
 */
const APPEARS_AT_DOC =
    '<when this asset appears, as the transcript word being spoken (e.g. "password") or milliseconds into the scene; absent means it is visible from the start of the scene>';

const colorAssetFormat = (): string => {
    const example: ColorAssetDescription = {
        type: ASSET_TYPES.BACKGROUND,
        name: '<asset_name>',
        bounds: '<x y width height>',
        color: '<color as hex, e.g. #ff0000>',
    };
    return '\n' + formatAsset(example);
};

const CaptionFormat = (): string => {
    const example: CaptionDescription = {
        type: ASSET_TYPES.CAPTION,
        name: '<asset_name>',
        bounds: '<x y width height>',
        sceneDesignId: '<the global caption design id applied to this scene group>',
    };
    return '\n' + formatAsset(example);
};

const textAssetFormat = (): string => {
    const example: DocumentationExample<TextAssetDescription> = {
        type: ASSET_TYPES.TEXT,
        name: '<asset_name>',
        bounds: '<x y width height>',
        text: '<text content>',
        alignment: `<${TextAlignment.LEFT} | ${TextAlignment.CENTER} | ${TextAlignment.RIGHT}>`,
        colors: '<normal and highlight text color as hex, e.g. #ff0000>',
        lines: '<number of text lines>',
        font: '<font family>',
        fontSize: '<font size>',
        appearsAt: APPEARS_AT_DOC,
    };
    return '\n' + formatAsset(example as TextAssetDescription);
};

const iconAssetFormat = (): string => {
    const example: IconAssetDescription = {
        type: ASSET_TYPES.ICON,
        name: '<asset_name>',
        bounds: '<x y width height>',
        mediaId: '<icon ID>',
        iconColors: '<comma-separated list of colors as hex, e.g. #ff0000>',
    };
    return '\n' + formatAsset(example);
};

const groupAssetFormat = (): string => {
    const example: GroupAssetDescription = {
        type: ASSET_TYPES.GROUP,
        name: '<asset_name>',
        bounds: '<x y width height>',
        children: '<comma-separated list of child asset names>',
        childrenAlignment: `<${VideoData.LEFT_ALIGNMENT} | ${VideoData.CENTER_ALIGNMENT} | ${VideoData.RIGHT_ALIGNMENT}>`,
        appearsAt: APPEARS_AT_DOC,
    };
    return '\n' + formatAsset(example);
};

const mediaAssetFormat = (): string => {
    const example: DocumentationExample<MediaAssetDescription> = {
        type: ASSET_TYPES.MEDIA,
        name: '<asset_name>',
        bounds: '<x y width height>',
        mediaId: '<media ID>',
        description: '<description>',
        mediaType: '<image/video/unknown>',
        position:
            '<x y> — where the media box sits on the canvas; distinct from mediaTranslation, which pans the media within its own frame',
        mediaScale: '<scale%>',
        mediaResolution: '<width height>',
        mediaTranslation: '<x(left|right|center) y(up|down|center)>',
        mediaXTranslationRange: '<[min, max]>',
        mediaYTranslationRange: '<[min, max]>',
        brightness: '<current brightness multiplier>',
    };
    return '\n' + formatAsset(example as MediaAssetDescription);
};

const stickerAssetFormat = (): string => {
    const example: StickerAssetDescription = {
        type: ASSET_TYPES.STICKER,
        name: '<asset_name>',
        bounds: '<x y width height>',
        mediaId: '<media ID>',
    };
    return '\n' + formatAsset(example);
};

export const getAssetAnnotationFormat = (): string => {
    return (
        groupAssetFormat() +
        textAssetFormat() +
        iconAssetFormat() +
        mediaAssetFormat() +
        stickerAssetFormat() +
        colorAssetFormat() +
        CaptionFormat()
    );
};

export const getHowOurVideosWorkGuide = (sceneAddressing: 'scene_id' | 'scene_index'): string => {
    // Toolbox addresses scenes by their stable scene_id; legacy surfaces address by positional
    // scene_index and consume an index-based representation, so the addressing guidance differs.
    const addressingBullet =
        sceneAddressing === 'scene_id'
            ? '- Every scene has a stable `scene_id` and every scene group a stable `scene_group_id`, each shown in its header (`@@ Scene scene_id <id> @@` and `@@ Scene Group <id> @@`). Always target a scene or scene group in tool calls by that id — never by a positional number, which shifts as scenes and groups are added. When inserting or moving a scene group, position it relative to an existing group with `after_scene_group_id` (omit it to place the group at the very start). Users are shown 1-based numbers in the UI, so use 1-based numbers when communicating with users.'
            : '- Scene indexes are 0-based but users are shown 1-based indexes in the UI. When referring to scenes, always use 0-based indexing internally and 1-based indexing when communicating with users.';
    return `## How our videos work

There are certain specifics to how our videos work that you should be aware of:
- Assets on a scene cannot be added or removed, only modified. A scene design is a fixed set of assets in a fixed layout, and what you can do to an asset depends on its type: TEXT — write, clear, and fully restyle; MEDIA — fill or swap the media and reframe it within its frame (scale, crop, Ken Burns, brightness), but the frame itself is fixed; ICON — swap the icon, and set when it appears; BACKGROUND — recolor. You can never add an asset a design doesn't have, remove one, move a media frame, or strip a built-in decorative graphic.
- Text assets with no text set are not displayed in the video.
- Videos must have at least one scene.
- Scene groups can be one of several types: "regular" (no audio — the on-screen text and media must communicate the whole message), "ai_voiceover" (AI-generated voiceover audio), "recorded_voiceover" (a human-recorded voiceover), "ai_talking_head" (an AI avatar speaking), or "recorded_talking_head" (a real person speaking on camera). Check the scene group type to understand how each part of the video communicates its message. A video can combine different group types (e.g. a text title scene, then a voiceover section, then a text outro).
${addressingBullet}
- "highlighted" text is displayed with "**" around it, indicating that it is rendered in a special way. Remove the "**" when you want to edit or display the text, as it is not part of the actual content.`;
};

export const getVideoAnnotationGuide = (): string => {
    return `## Video Components & Assets
${getVideoAnnotationFormat()}
`;
};

export const getVideoAnnotationFormat = (): string => {
    return `
- SCENE GROUP: groups one or more scenes that share timing (e.g., voiceover, talking head)
  - Types: regular (no audio, text), ai_voiceover, recorded_voiceover, ai_talking_head (AI avatar), recorded_talking_head
  - Contains: duration and — for AI voiceover groups — the voice and voiceSpeed
- SCENE: contains assets that define the layout and content
  - Scenes are listed under their scene group's header; each scene belongs to exactly one group
  - Serialized with: design (its scene design id), duration, subscenes (count), covers (voice-driven groups only: the transcript words this scene plays over and its role — overlay, uncovered, or speaker on screen), textPositionPresets, and its assets
- Asset types include:
  - ${ASSET_TYPES.TEXT}: editable on-screen text
  - ${ASSET_TYPES.CAPTION}: on-screen subtitles for transcript, not directly editable (applies to full scene group)
  - ${ASSET_TYPES.FANCY_CAPTION}: on-screen animated text that displays transcript, not directly editable (applies to single scene)
  - ${ASSET_TYPES.MEDIA}: editable image or video 
  - ${ASSET_TYPES.ICON}: editable icon graphic
  - ${ASSET_TYPES.GROUP}: non-visible alignment container for child assets
  - ${ASSET_TYPES.WATERMARK}: watermark image
  - ${ASSET_TYPES.BACKGROUND}: solid background color
  - ${ASSET_TYPES.MOTION_GRAPHIC}: a bespoke animated graphic (dynamic scene) authored as an HTML composition; serialized with its addressable parts and editable texts, or status "empty (awaiting generation)" before it has content`;
};

export const getDiffFormatGuide = (): string => {
    return `## Understanding Video State Diffs

Tools that modify the video automatically return a git-style diff showing what changed. This helps you track the evolution of the video over time.

### Diff Format Symbols
- \`+\` prefix: Property or asset was **added**
- \`-\` prefix: Property or asset was **removed**
- No prefix (context lines): Shows which asset is being modified
- \`@@ Section Name @@\`: Header indicating which part of video changed

### When You See Diffs
- **Initial state**: All properties shown with \`+\` (establishing baseline)
- **After tool execution**: Only changed properties shown with \`-\` (old) and \`+\` (new)
- **Modified assets**: Use shorthand \`asset: name\` to identify which asset changed

**Important**: Every tool that modifies the video will return a diff in this format. Use these diffs to track what changed and build your understanding of the current video state.`;
};
